// ui/src/components/manager/TeamCompensation.jsx
/**
 * @file TeamCompensation.jsx
 * @module ui/src/components/manager/TeamCompensation
 * @description Renders the manager's primary team compensation view with summary metrics,
 * local filtering/sorting (ic path) or server-side hierarchy tree (rollup/hybrid path),
 * coverage escalation, rep drill-down, and person-scoped draw access.
 *
 * S803 — OI-S802-MANAGER-HIERARCHY-ROLLUP:
 *   data.hierarchyMode === 'direct'    → flat table (IC manager, bounded, client-side OK)
 *   data.hierarchyMode === 'recursive' → hierarchy tree (rollup/hybrid manager)
 *     DEPTH_INDENT: depth 0=pl-0 (isSelf), 1=pl-0 (direct reports), 2=pl-6, 3=pl-12, etc.
 *     calcModeLabel: rollup=blue info, hybrid=primary, ic=muted, null=nothing
 *     toggleNode / getHiddenIds: collapse/expand per-node chevron
 *     isSelf row: hybrid manager's own row, depth=0, styled distinctly at top
 *     IntersectionObserver sentinel: auto-loads next page when scrolled to bottom
 *     Server-side pagination: cursor param, limit=50 default
 *   Ported from ManagerShell.jsx TeamPayout (DEPTH_INDENT, calcModeLabel,
 *   toggleNode, getHiddenIds — lines 799-881 of ManagerShell.jsx confirmed S803).
 *
 * @session-6-migration-flags
 * - draw_ledger.participant_uuid (m_tc_10): NOT IN DDL r57. Manager draw-ledger
 *   access uses currentUser.personUuid as the person-scoped continuity key.
 * - draw_ledger_entry (m_tc_11): NOT IN DDL r57. Full ledger rendering remains
 *   delegated to C-58.
 * - plan.effective_from / effective_to (m_tc_01): Indirect dependency through C-35.
 *
 * @locked-rules
 * - INV-6: Draw balances are person-scoped, never plan-version scoped.
 * - INV-3: planStatus is never used as a temporal signal.
 * - DP-8: Amendment display is document-level only.
 * - XC-6: Team and rep provisional payouts preserve calculated zero, unavailable, and partial.
 * - XC-7: Coverage, period, and amendment states are explicitly labeled.
 * - DP-10: Escalation and other server mutations are delegated; no optimistic mutation occurs here.
 *
 * @accessibility
 * - Summary cards are named regions.
 * - Team table uses scoped column headings (flat path).
 * - Hierarchy tree uses role="list" / role="listitem" with aria-expanded on nodes.
 * - Filters expose aria-pressed.
 * - Rep detail and draw ledger use aria-modal dialogs with focus trap, Escape close,
 *   and focus restoration.
 *
 * @dependencies
 * - C-35 RepCompDetail.
 * - C-36 CoverageExceptionCard.
 * - C-44 ProvisionalBadge.
 * - C-46 DataFreshnessIndicator.
 * - C-58 PersonDrawLedger.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import CoverageExceptionCard from './CoverageExceptionCard';
import ProvisionalBadge from '../shared/ProvisionalBadge';
import DataFreshnessIndicator from '../shared/DataFreshnessIndicator';
import PersonDrawLedger from '../shared/PersonDrawLedger';
import PayoutExplanationPanel from '../shared/PayoutExplanationPanel';

// ---------------------------------------------------------------------------
// S803 — Hierarchy constants (ported from ManagerShell.jsx lines 799-808)
// DEPTH_INDENT: depth→Tailwind left-padding class.
//   depth=0: isSelf row (hybrid manager's own row) — no indent, pl-0
//   depth=1: direct reports — no indent, pl-0
//   depth=2: their reports — pl-6
//   depth=3: two levels deep — pl-12
//   depth N≥4: pl-12 cap (matches HIERARCHY_DEPTH_LIMIT=10 but visual cap at 3)
// ---------------------------------------------------------------------------

const DEPTH_INDENT = {
  0: 'pl-0',   // isSelf row
  1: 'pl-0',   // direct reports
  2: 'pl-6',   // depth 2
  3: 'pl-12',  // depth 3+
};

function depthIndentClass(depth) {
  return DEPTH_INDENT[Math.min(depth, 3)] ?? 'pl-12';
}

// calcModeLabel: returns { label, cls } for mode pill, null for unknown/absent
// Ported from ManagerShell.jsx line 802-808
function calcModeLabel(mode) {
  if (mode === 'rollup')       return { label: 'Rollup', cls: 'tc-pill tc-pill--rollup' };
  if (mode === 'hybrid')       return { label: 'Hybrid', cls: 'tc-pill tc-pill--hybrid' };
  if (mode === 'ic')           return { label: 'IC',     cls: 'tc-pill tc-pill--ic' };
  if (mode === 'quota_exempt') return { label: 'Exempt', cls: 'tc-pill tc-pill--exempt' };
  return null;
}

// ---------------------------------------------------------------------------
// Flat-path filters and sort options (ic manager path only)
// ---------------------------------------------------------------------------

const FILTERS = [
  { value: 'all',       label: 'All' },
  { value: 'exception', label: 'With exceptions' },
  { value: 'covered',   label: 'Covered' },
];

// ---------------------------------------------------------------------------
// Shared formatting helpers
// ---------------------------------------------------------------------------

function formatCurrency(value, currencyCode = 'USD') {
  if (value === null || value === undefined) return '—';
  // Guard: default param only catches undefined — explicit null still reaches here.
  // Reps with no payout_currency_code (no completed run) return null from router.
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode || 'USD',
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value) {
  if (value === null || value === undefined) return '—';
  return `${new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(value)}%`;
}

function titleCase(value) {
  if (!value) return 'Unknown';
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function coverageLabel(rep) {
  if (rep.coverageStatus === 'covered' && !rep.hasException) return 'Covered';
  if (rep.coverageStatus === 'unassigned') return 'Unassigned';
  return 'Exception';
}

// ---------------------------------------------------------------------------
// Focus trap hook (unchanged from S802)
// ---------------------------------------------------------------------------

function useFocusTrap(open, dialogRef, restoreRef, onClose) {
  useEffect(() => {
    if (!open || !dialogRef.current) return undefined;

    const dialog = dialogRef.current;
    const previousFocus = restoreRef.current;

    const getFocusable = () =>
      Array.from(
        dialog.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );

    const focusables = getFocusable();
    (focusables[0] || dialog).focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const currentFocusable = getFocusable();
      if (currentFocusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = currentFocusable[0];
      const last  = currentFocusable[currentFocusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    dialog.addEventListener('keydown', handleKeyDown);

    return () => {
      dialog.removeEventListener('keydown', handleKeyDown);
      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus();
      }
    };
  }, [open, dialogRef, restoreRef, onClose]);
}

// ---------------------------------------------------------------------------
// S803 — Hierarchy tree helpers (ported from ManagerShell.jsx lines 860-881)
// toggleNode: toggle collapse state for a node
// getHiddenIds: derive set of hidden position_ids based on collapsed nodes
// ---------------------------------------------------------------------------

function useHierarchyCollapse() {
  const [collapsedNodes, setCollapsedNodes] = useState(new Set());

  const toggleNode = useCallback((positionId) => {
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(positionId)) {
        next.delete(positionId);
      } else {
        next.add(positionId);
      }
      return next;
    });
  }, []);

  return { collapsedNodes, toggleNode };
}

function getHiddenIds(reps, collapsedNodes) {
  // Ported from ManagerShell.jsx get_hidden_ids (line 868).
  // Traverses reps in DFS order (server guarantees parent-before-children).
  // Suppresses children of collapsed nodes based on depth comparison.
  const hidden = new Set();
  let suppressFromDepth = null;

  for (const rep of reps) {
    if (suppressFromDepth !== null) {
      if (rep.depth > suppressFromDepth) {
        hidden.add(rep.positionId);
        continue;
      } else {
        suppressFromDepth = null;
      }
    }
    if (collapsedNodes.has(rep.positionId) && rep.hasDirectReports) {
      suppressFromDepth = rep.depth;
    }
  }

  return hidden;
}

// ---------------------------------------------------------------------------
// TeamCompensation component
// ---------------------------------------------------------------------------

function TeamCompensation({
  managerId,
  fiscalPeriodId,
  currentUser,
  onEscalate,
}) {
  // Core data state
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  // Pagination state (recursive path only)
  const [allReps, setAllReps]         = useState([]);
  const [nextCursor, setNextCursor]   = useState(null);
  const [hasMore, setHasMore]         = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Hierarchy collapse state (recursive path)
  const { collapsedNodes, toggleNode } = useHierarchyCollapse();

  // UI state
  const [selectedRepForExplanation, setSelectedRepForExplanation] = useState(null); // { payoutSummaryId, positionName, roleCode }
  const [highlightedPositionId, setHighlightedPositionId] = useState(null);
  const [drawLedgerOpen, setDrawLedgerOpen] = useState(false);

  // Flat-path filter/sort/search state (ic manager path only)
  const [filter, setFilter]           = useState('all');
  const [sortBy, setSortBy]           = useState('attainment_desc');
  const [searchQuery, setSearchQuery] = useState('');
  const searchDebounceRef = useRef(null);

  // Dialog refs
  const drawDialogRef  = useRef(null);
  const drawTriggerRef = useRef(null);

  // IntersectionObserver sentinel ref (recursive path pagination)
  const sentinelRef = useRef(null);

  const closeRepExplanation = () => setSelectedRepForExplanation(null);
  const closeDrawLedger = () => setDrawLedgerOpen(false);

  useFocusTrap(drawLedgerOpen, drawDialogRef, drawTriggerRef, closeDrawLedger);

  // ---------------------------------------------------------------------------
  // Initial data fetch
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    setError(null);
    setAllReps([]);
    setNextCursor(null);
    setHasMore(false);

    fetch(
      `/api/v1/manager/${encodeURIComponent(managerId)}/team-summary?period=${encodeURIComponent(fiscalPeriodId)}`,
      { signal: controller.signal }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load team compensation (${response.status}).`);
        }
        return response.json();
      })
      .then((payload) => {
        setData(payload);
        setAllReps(payload.reps ?? []);
        setNextCursor(payload.pagination?.nextCursor ?? null);
        setHasMore(payload.pagination?.hasMore ?? false);
      })
      .catch((fetchError) => {
        if (fetchError.name !== 'AbortError') {
          setError(fetchError);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [managerId, fiscalPeriodId, retryKey]);

  // ---------------------------------------------------------------------------
  // S803 — Load next page (recursive path, IntersectionObserver triggered)
  // ---------------------------------------------------------------------------

  const loadNextPage = useCallback(() => {
    if (!hasMore || loadingMore || !nextCursor) return;

    setLoadingMore(true);

    fetch(
      `/api/v1/manager/${encodeURIComponent(managerId)}/team-summary?period=${encodeURIComponent(fiscalPeriodId)}&cursor=${encodeURIComponent(nextCursor)}`,
    )
      .then((response) => {
        if (!response.ok) throw new Error(`Pagination load failed (${response.status}).`);
        return response.json();
      })
      .then((payload) => {
        setAllReps((prev) => [...prev, ...(payload.reps ?? [])]);
        setNextCursor(payload.pagination?.nextCursor ?? null);
        setHasMore(payload.pagination?.hasMore ?? false);
      })
      .catch((err) => {
        // Non-fatal: log, leave hasMore=true so user can retry by scrolling
        console.error('TeamCompensation pagination error:', err);
      })
      .finally(() => {
        setLoadingMore(false);
      });
  }, [managerId, fiscalPeriodId, hasMore, loadingMore, nextCursor]);

  // ---------------------------------------------------------------------------
  // S803 — IntersectionObserver on sentinel div (recursive path)
  // Auto-triggers loadNextPage when sentinel scrolls into view
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!sentinelRef.current) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadNextPage();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadNextPage]);

  // ---------------------------------------------------------------------------
  // Flat-path visible reps (ic manager — client-side filter/sort/search)
  // ---------------------------------------------------------------------------

  const visibleRepsFlat = useMemo(() => {
    if (!data || data.hierarchyMode !== 'direct') return [];

    let reps = [...allReps];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      reps = reps.filter(
        (r) =>
          r.fullName?.toLowerCase().includes(q) ||
          r.roleCode?.toLowerCase().includes(q) ||
          r.segmentCode?.toLowerCase().includes(q)
      );
    }

    if (filter === 'exception') {
      reps = reps.filter((rep) => rep.hasException || rep.coverageStatus !== 'covered');
    } else if (filter === 'covered') {
      reps = reps.filter((rep) => !rep.hasException && rep.coverageStatus === 'covered');
    }

    if (sortBy === 'attainment_desc') {
      reps.sort((l, r) => {
        const lv = l.attainmentPct == null ? Number.NEGATIVE_INFINITY : l.attainmentPct;
        const rv = r.attainmentPct == null ? Number.NEGATIVE_INFINITY : r.attainmentPct;
        return rv - lv;
      });
    } else if (sortBy === 'attainment_asc') {
      reps.sort((l, r) => {
        const lv = l.attainmentPct == null ? Number.POSITIVE_INFINITY : l.attainmentPct;
        const rv = r.attainmentPct == null ? Number.POSITIVE_INFINITY : r.attainmentPct;
        return lv - rv;
      });
    } else if (sortBy === 'payout_desc') {
      reps.sort((a, b) => (b.provisionalPayout ?? 0) - (a.provisionalPayout ?? 0));
    } else if (sortBy === 'payout_asc') {
      reps.sort((a, b) => (a.provisionalPayout ?? 0) - (b.provisionalPayout ?? 0));
    } else if (sortBy === 'name_asc') {
      reps.sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? ''));
    } else if (sortBy === 'quota_desc') {
      reps.sort((a, b) => (b.quota ?? 0) - (a.quota ?? 0));
    }

    return reps;
  }, [data, allReps, filter, sortBy, searchQuery]);

  // ---------------------------------------------------------------------------
  // Recursive-path visible reps (rollup/hybrid — server DFS order, collapse applied)
  // ---------------------------------------------------------------------------

  const visibleRepsTree = useMemo(() => {
    if (!data || data.hierarchyMode !== 'recursive') return [];
    const hiddenIds = getHiddenIds(allReps, collapsedNodes);
    return allReps.filter((rep) => !hiddenIds.has(rep.positionId));
  }, [data, allReps, collapsedNodes]);

  // ---------------------------------------------------------------------------
  // Row interaction handlers
  // ---------------------------------------------------------------------------

  const handleEscalate = (escalationContext) => {
    if (onEscalate) onEscalate(escalationContext);
  };

  const openRepDetail = (rep) => {
    setHighlightedPositionId(rep.positionId);
    if (rep.payoutSummaryId) {
      setSelectedRepForExplanation({
        payoutSummaryId: rep.payoutSummaryId,
        positionName:    rep.fullName,
        roleCode:        rep.roleCode ?? rep.calcMode,
      });
    }
  };

  const openDrawLedger = (event) => {
    drawTriggerRef.current = event.currentTarget;
    setDrawLedgerOpen(true);
  };

  // ---------------------------------------------------------------------------
  // Loading / error / empty states
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <main className="team-compensation" aria-label="Team compensation" aria-busy="true">
        <div className="team-compensation__summary-grid">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skeleton team-compensation__card-skeleton" />
          ))}
        </div>
        <div className="team-compensation__table-skeleton">
          <div className="skeleton team-compensation__table-head-skeleton" />
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton team-compensation__row-skeleton" />
          ))}
        </div>
        <style>{styles}</style>
      </main>
    );
  }

  if (error) {
    return (
      <main className="team-compensation">
        <section className="team-compensation__error" role="alert" data-state="error">
          <h2>Unable to load team compensation</h2>
          <p>{error.message}</p>
          <button
            type="button"
            className="secondary-button"
            onClick={() => setRetryKey((v) => v + 1)}
          >
            Retry
          </button>
        </section>
        <style>{styles}</style>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="team-compensation">
        <div className="team-compensation__empty" data-state="unavailable">
          Team compensation information is not available.
        </div>
        <style>{styles}</style>
      </main>
    );
  }

  const summary = data.teamSummary;
  const isRecursive = data.hierarchyMode === 'recursive';
  // S1045: hqCurrencyCode from fx_rate_set.reporting_currency — never hardcoded
  const hqCcy = summary.hqCurrencyCode ?? 'USD';
  const teamPayoutAvailable = summary.provisionalTeamPayoutHq !== null && summary.provisionalTeamPayoutHq !== undefined;
  const teamPayoutPartial   = summary.provisionalState === 'partial';

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <main className="team-compensation" aria-labelledby="team-compensation-title">

      {/* Header */}
      <header className="team-compensation__header">
        <div>
          <p className="team-compensation__eyebrow">Manager view</p>
          <h1 id="team-compensation-title">Team compensation</h1>
          <p>{data.period.periodLabel}</p>
        </div>
        <div className="team-compensation__header-signals">
          <span
            className={`team-compensation__period-status team-compensation__period-status--${data.period.periodStatus}`}
            data-state={data.period.periodStatus}
          >
            {titleCase(data.period.periodStatus)}
          </span>
          {isRecursive && data.totalTeamSize != null && (
            <span className="team-compensation__team-size">
              {data.totalTeamSize} in hierarchy
            </span>
          )}
          <DataFreshnessIndicator
            signal="credits"
            freshnessAt={summary.creditFreshnessAt}
            status={summary.creditFreshnessStatus}
          />
        </div>
      </header>

      {/* Summary cards */}
      <div className="team-compensation__summary-grid">
        <section className="team-compensation__summary-card" role="region" aria-label="Team Quota Attainment">
          <p className="team-compensation__card-label">Team Quota Attainment</p>
          <strong>{formatPercent(summary.teamAttainmentPct)}</strong>
          <span>Team quota {formatCurrency(summary.totalQuotaHq, hqCcy)}</span>
        </section>

        <section
          className="team-compensation__summary-card"
          role="region"
          aria-label="Team Provisional Payout"
          data-state={!teamPayoutAvailable ? 'unavailable' : teamPayoutPartial ? 'partial' : 'calculated'}
        >
          <div className="team-compensation__card-topline">
            <p className="team-compensation__card-label">Team Provisional Payout</p>
            {summary.freshnessAt ? (
              <ProvisionalBadge
                freshnessTimestamp={summary.freshnessAt}
                label="Provisional"
                completenessState={summary.provisionalState === 'full' ? 'full' : 'partial'}
              />
            ) : null}
          </div>
          <strong>
            {teamPayoutAvailable
              ? formatCurrency(summary.provisionalTeamPayoutHq, hqCcy)
              : 'Not available'}
          </strong>
          {teamPayoutPartial && teamPayoutAvailable ? (
            <span className="team-compensation__partial">Partial</span>
          ) : null}
        </section>

        <section className="team-compensation__summary-card" role="region" aria-label="Team Coverage">
          <p className="team-compensation__card-label">Coverage</p>
          <strong>{summary.repsCovered}/{summary.repsTotal}</strong>
          <span>reps covered</span>
          {summary.coverageExceptions > 0 ? (
            <span className="team-compensation__exception-count" data-state="exceptions">
              {summary.coverageExceptions}{' '}
              {summary.coverageExceptions === 1 ? 'exception' : 'exceptions'}
            </span>
          ) : (
            <span className="team-compensation__all-covered" data-state="covered">✓ All covered</span>
          )}
        </section>

        <section className="team-compensation__summary-card" role="region" aria-label="Team Draw Summary">
          <p className="team-compensation__card-label">Team draw net balance</p>
          <strong>{formatCurrency(summary.drawNetBalance, summary.drawCurrencyCode)}</strong>
          <span>Person-level continuity across plan amendments</span>
          <button
            ref={drawTriggerRef}
            type="button"
            className="text-button team-compensation__draw-link"
            onClick={openDrawLedger}
          >
            View draw ledger
          </button>
        </section>
      </div>

      {/* Coverage exceptions */}
      {Array.isArray(data.exceptions) && data.exceptions.length > 0 ? (
        <section aria-label="Coverage exceptions">
          <CoverageExceptionCard exceptions={data.exceptions} onEscalate={handleEscalate} />
        </section>
      ) : null}

      {/* Team section */}
      <section className="team-compensation__team-section" aria-labelledby="team-compensation-reps-title">
        <div className="team-compensation__team-toolbar">
          <div>
            <p className="team-compensation__eyebrow">Team members</p>
            <h2 id="team-compensation-reps-title">Rep compensation</h2>
          </div>

          {/* Flat-path controls (ic manager only) */}
          {!isRecursive && (
            <div className="team-compensation__controls">
              <div className="team-compensation__filters" aria-label="Filter team members">
                {FILTERS.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    className="team-compensation__filter"
                    aria-pressed={filter === item.value}
                    data-state={filter === item.value ? 'selected' : 'idle'}
                    onClick={() => setFilter(item.value)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <input
                type="search"
                placeholder="Search by name, role…"
                value={searchQuery}
                onChange={(e) => {
                  const v = e.target.value;
                  clearTimeout(searchDebounceRef.current);
                  searchDebounceRef.current = setTimeout(() => setSearchQuery(v), 300);
                }}
                style={{
                  fontSize: '12px', padding: '4px 8px', borderRadius: '6px',
                  border: '1px solid rgb(var(--color-border))',
                  background: 'rgb(var(--color-surface-muted))',
                  color: 'rgb(var(--color-text))',
                  width: '180px', outline: 'none',
                }}
                aria-label="Search team members"
              />

              <label className="team-compensation__sort">
                <span>Sort</span>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="attainment_desc">Attainment: high to low</option>
                  <option value="attainment_asc">Attainment: low to high</option>
                  <option value="payout_desc">Payout: high to low</option>
                  <option value="payout_asc">Payout: low to high</option>
                  <option value="name_asc">Name: A to Z</option>
                  <option value="quota_desc">Quota: high to low</option>
                </select>
              </label>
            </div>
          )}

          {/* Recursive-path header info (rollup/hybrid) */}
          {isRecursive && (
            <div className="team-compensation__hierarchy-badge">
              <span className="team-compensation__hierarchy-label">
                Hierarchy view · {data.totalTeamSize ?? '—'} positions
              </span>
            </div>
          )}
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* PATH A — Flat table (ic manager, hierarchyMode='direct')           */}
        {/* ----------------------------------------------------------------- */}

        {!isRecursive && (
          <>
            {visibleRepsFlat.length === 0 ? (
              <div className="team-compensation__empty" data-state="empty-filter">
                No reps match the current filter.
              </div>
            ) : (
              <div style={{ overflowY: 'auto', maxHeight: '60vh', overflowX: 'auto' }}>
                <table
                  className="team-compensation__table"
                  role="table"
                  aria-label="Team compensation table"
                >
                  <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: 'rgb(var(--color-surface))' }}>
                    <tr>
                      {[
                        { label: 'Name',               sort: 'name_asc'        },
                        { label: 'Role',               sort: null               },
                        { label: `Quota (${hqCcy})`,   sort: 'quota_desc'      },
                        { label: `Credit (${hqCcy})`,  sort: null               },
                        { label: 'Attainment %',       sort: 'attainment_desc' },
                        { label: `Payout (${hqCcy})`,  sort: 'payout_desc'     },
                        { label: 'Coverage',           sort: null               },
                        { label: 'Amendment',          sort: null               },
                      ].map(({ label, sort: colSort }) => (
                        <th
                          key={label}
                          scope="col"
                          onClick={colSort ? () => setSortBy((s) =>
                            s === colSort
                              ? colSort.replace('_desc', '_asc').replace('_asc', '_desc')
                              : colSort
                          ) : undefined}
                          style={colSort ? { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' } : {}}
                          aria-sort={
                            colSort && sortBy.startsWith(colSort.replace(/_desc|_asc/, ''))
                              ? sortBy.endsWith('_desc') ? 'descending' : 'ascending'
                              : undefined
                          }
                        >
                          {label}
                          {colSort && sortBy.startsWith(colSort.replace(/_desc|_asc/, '')) && (
                            <span style={{ marginLeft: '4px', fontSize: '10px' }}>
                              {sortBy.endsWith('_desc') ? '▼' : '▲'}
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRepsFlat.map((rep) => {
                      const payoutAvailable = rep.provisionalPayout !== null && rep.provisionalPayout !== undefined;
                      const partial         = rep.provisionalState === 'partial';
                      const coverage        = coverageLabel(rep);
                      const isHighlighted   = highlightedPositionId === rep.positionId;

                      return (
                        <tr
                          key={rep.positionId}
                          data-state={rep.hasException ? 'exception' : 'covered'}
                          style={{
                            cursor: 'pointer',
                            background:   isHighlighted ? 'rgb(var(--color-primary) / 0.1)' : undefined,
                            outline:      isHighlighted ? '2px solid rgb(var(--color-primary) / 0.4)' : undefined,
                            outlineOffset: '-1px',
                          }}
                          onClick={(event) => {
                            if (event.target.closest('button, a')) return;
                            openRepDetail(rep);
                          }}
                        >
                          <td>
                            <strong>{rep.fullName}</strong>
                          </td>
                          <td>{rep.roleCode}</td>
                          <td>{formatCurrency(rep.quotaHq, rep.hqCurrencyCode ?? hqCcy)}</td>
                          <td>{formatCurrency(rep.creditHq, rep.hqCurrencyCode ?? hqCcy)}</td>
                          <td>{formatPercent(rep.attainmentPct)}</td>
                          <td>
                            <div
                              className="team-compensation__payout-cell"
                              data-state={!payoutAvailable ? 'unavailable' : partial ? 'partial' : 'calculated'}
                            >
                              <span>
                                {payoutAvailable ? formatCurrency(rep.payoutHq, rep.hqCurrencyCode ?? hqCcy) : '—'}
                              </span>
                              {partial && payoutAvailable && rep.freshnessAt ? (
                                <span
                                  title={`Provisional · Updated ${new Date(rep.freshnessAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'rgb(var(--color-text-muted))' }}
                                >
                                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'rgb(var(--color-warning))', flexShrink: 0 }} />
                                  {`Updated ${new Date(rep.freshnessAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            <span
                              className={`team-compensation__coverage-state team-compensation__coverage-state--${coverage.toLowerCase().replace(/\s+/g, '-')}`}
                              data-state={coverage === 'Covered' ? 'covered' : coverage === 'Unassigned' ? 'unassigned' : 'exception'}
                            >
                              {coverage}
                            </span>
                          </td>
                          <td>
                            {rep.amendmentInFlight === true ? (
                              <span className="team-compensation__amendment-state" data-state="amendment-pending">
                                Amendment pending
                              </span>
                            ) : (
                              <span className="team-compensation__cell-muted">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {/* Totals footer — tfoot inside same table ensures column alignment.
                      Server-side aggregates from teamSummary — full team total, not filtered rows. */}
                  <tfoot>
                    <tr className="team-compensation__totals-row">
                      <td className="team-compensation__totals-label-cell">Team Total</td>
                      <td />
                      <td className="team-compensation__totals-value-cell">
                        {summary.totalQuotaHq != null
                          ? formatCurrency(summary.totalQuotaHq, hqCcy)
                          : '—'}
                      </td>
                      <td className="team-compensation__totals-value-cell">
                        {summary.teamCreditTotalHq != null
                          ? formatCurrency(summary.teamCreditTotalHq, hqCcy)
                          : '—'}
                      </td>
                      <td className="team-compensation__totals-value-cell">
                        {summary.teamAttainmentPct != null
                          ? formatPercent(summary.teamAttainmentPct)
                          : '—'}
                      </td>
                      <td className="team-compensation__totals-value-cell">
                        {summary.provisionalTeamPayoutHq != null
                          ? formatCurrency(summary.provisionalTeamPayoutHq, hqCcy)
                          : '—'}
                      </td>
                      <td />
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </>
        )}

        {/* ----------------------------------------------------------------- */}
        {/* PATH B/C — Hierarchy tree (rollup/hybrid, hierarchyMode='recursive') */}
        {/* ----------------------------------------------------------------- */}

        {isRecursive && (
          <>
            {visibleRepsTree.length === 0 && !loadingMore ? (
              <div className="team-compensation__empty" data-state="empty-hierarchy">
                No team members found in hierarchy.
              </div>
            ) : (
              <div
                className="team-compensation__hierarchy-scroll"
                style={{ overflowY: 'auto', maxHeight: '60vh' }}
                role="list"
                aria-label="Team hierarchy"
              >
                {/* Header row */}
                <div className="team-compensation__hierarchy-header">
                  <span className="team-compensation__hierarchy-col--name">Name</span>
                  <span className="team-compensation__hierarchy-col--quota">Quota ({hqCcy})</span>
                  <span className="team-compensation__hierarchy-col--attain">Attainment</span>
                  <span className="team-compensation__hierarchy-col--payout">Payout ({hqCcy})</span>
                  <span className="team-compensation__hierarchy-col--coverage">Coverage</span>
                </div>

                {visibleRepsTree.map((rep) => {
                  const isCollapsed    = collapsedNodes.has(rep.positionId);
                  const isNode         = rep.hasDirectReports;
                  const isSelf         = rep.isSelf;
                  const isHighlighted  = highlightedPositionId === rep.positionId;
                  const indentClass    = depthIndentClass(rep.depth);
                  const modeTag        = calcModeLabel(rep.calculationMode);
                  const payoutAvail    = rep.provisionalPayout !== null && rep.provisionalPayout !== undefined;
                  const partial        = rep.provisionalState === 'partial';
                  const coverage       = coverageLabel(rep);

                  return (
                    <div
                      key={rep.positionId}
                      className={`${indentClass} team-compensation__hierarchy-row-wrap`}
                      role="listitem"
                    >
                      <div
                        className={[
                          'team-compensation__hierarchy-row',
                          isSelf         ? 'team-compensation__hierarchy-row--self'        : '',
                          isHighlighted  ? 'team-compensation__hierarchy-row--highlighted' : '',
                          rep.hasException ? 'team-compensation__hierarchy-row--exception' : '',
                        ].filter(Boolean).join(' ')}
                      >
                        {/* Collapse chevron or spacer */}
                        {isNode ? (
                          <button
                            type="button"
                            className="team-compensation__hierarchy-chevron"
                            aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                            aria-expanded={!isCollapsed}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleNode(rep.positionId);
                            }}
                          >
                            <span
                              className="team-compensation__chevron-icon"
                              style={{ transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)' }}
                            >
                              ›
                            </span>
                          </button>
                        ) : (
                          <div className="team-compensation__hierarchy-chevron-spacer" />
                        )}

                        {/* Row content — clickable */}
                        <button
                          type="button"
                          className="team-compensation__hierarchy-row-btn"
                          onClick={() => openRepDetail(rep)}
                          aria-label={`View ${rep.fullName} compensation detail`}
                        >
                          {/* Name column */}
                          <span className="team-compensation__hierarchy-col--name">
                            {isSelf && (
                              <span className="team-compensation__self-badge">My Performance</span>
                            )}
                            <span className={`team-compensation__hierarchy-name${isSelf ? ' team-compensation__hierarchy-name--self' : ''}`}>
                              {rep.fullName}
                            </span>
                            {modeTag && (
                              <span className={modeTag.cls}>{modeTag.label}</span>
                            )}
                            <span className="team-compensation__cell-meta">{rep.segmentCode}</span>
                          </span>

                          {/* Quota */}
                          <span className="team-compensation__hierarchy-col--quota">
                            {formatCurrency(rep.quotaHq, rep.hqCurrencyCode ?? hqCcy)}
                          </span>

                          {/* Attainment */}
                          <span className="team-compensation__hierarchy-col--attain">
                            {formatPercent(rep.attainmentPct)}
                          </span>

                          {/* Payout */}
                          <span className="team-compensation__hierarchy-col--payout">
                            <div
                              className="team-compensation__payout-cell"
                              data-state={!payoutAvail ? 'unavailable' : partial ? 'partial' : 'calculated'}
                            >
                              <span>
                                {payoutAvail ? formatCurrency(rep.payoutHq, rep.hqCurrencyCode ?? hqCcy) : '—'}
                              </span>
                              {partial && payoutAvail && rep.freshnessAt ? (
                                <span
                                  title={`Provisional · Updated ${new Date(rep.freshnessAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'rgb(var(--color-text-muted))' }}
                                >
                                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'rgb(var(--color-warning))', flexShrink: 0 }} />
                                  Provisional
                                </span>
                              ) : null}
                            </div>
                          </span>

                          {/* Coverage */}
                          <span className="team-compensation__hierarchy-col--coverage">
                            {!isSelf && (
                              <span
                                className={`team-compensation__coverage-state team-compensation__coverage-state--${coverage.toLowerCase().replace(/\s+/g, '-')}`}
                                data-state={coverage === 'Covered' ? 'covered' : coverage === 'Unassigned' ? 'unassigned' : 'exception'}
                              >
                                {coverage}
                              </span>
                            )}
                          </span>
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* IntersectionObserver sentinel — triggers loadNextPage */}
                {hasMore && (
                  <div
                    ref={sentinelRef}
                    className="team-compensation__pagination-sentinel"
                    aria-hidden="true"
                  >
                    {loadingMore && (
                      <div className="team-compensation__loading-more">
                        Loading more…
                      </div>
                    )}
                  </div>
                )}

                {/* End of hierarchy indicator */}
                {!hasMore && allReps.length > 0 && (
                  <div className="team-compensation__hierarchy-end">
                    All {data.totalTeamSize} positions loaded
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </section>

      {/* Payout explanation panel — full layered calculation detail. */}
      {selectedRepForExplanation?.payoutSummaryId ? (
        <PayoutExplanationPanel
          payoutSummaryId={selectedRepForExplanation.payoutSummaryId}
          positionName={selectedRepForExplanation.positionName}
          roleCode={selectedRepForExplanation.roleCode}
          onClose={closeRepExplanation}
        />
      ) : null}

      {/* Draw ledger dialog */}
      {drawLedgerOpen ? (
        <div
          className="team-compensation__dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeDrawLedger();
          }}
        >
          <div
            ref={drawDialogRef}
            className="team-compensation__dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Manager draw ledger"
            tabIndex={-1}
          >
            <header className="team-compensation__dialog-header">
              <div>
                <h2>Draw ledger</h2>
                <p>Balance continues across plan amendments.</p>
              </div>
              <button
                type="button"
                className="icon-button"
                aria-label="Close draw ledger"
                onClick={closeDrawLedger}
              >
                ×
              </button>
            </header>
            <div className="team-compensation__dialog-body">
              <PersonDrawLedger
                personUuid={data?.managerPersonUuid ?? currentUser.personUuid}
                fiscalPeriodId={fiscalPeriodId}
                mode="manager"
              />
            </div>
          </div>
        </div>
      ) : null}

      <style>{styles}</style>
    </main>
  );
}

TeamCompensation.propTypes = {
  managerId:      PropTypes.string.isRequired,
  fiscalPeriodId: PropTypes.number.isRequired,
  currentUser: PropTypes.shape({
    positionId: PropTypes.string.isRequired,
    personUuid: PropTypes.string.isRequired,
  }).isRequired,
  onEscalate: PropTypes.func,
};

TeamCompensation.defaultProps = {
  onEscalate: undefined,
};

// ---------------------------------------------------------------------------
// Styles — AH-CSS-01: custom CSS classes only, no Tailwind variants on them
// ---------------------------------------------------------------------------

const styles = `
  .team-compensation {
    display: flex;
    flex-direction: column;
    gap: 16px;
    color: rgb(var(--color-text));
  }

  .team-compensation__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  .team-compensation__eyebrow {
    margin: 0 0 2px;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .team-compensation__header h1 {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
  }

  .team-compensation__header > div > p:last-child {
    margin: 3px 0 0;
    color: rgb(var(--color-text-muted));
    font-size: 12px;
  }

  .team-compensation__header-signals {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
  }

  .team-compensation__team-size {
    padding: 2px 10px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 999px;
    background: rgb(var(--color-surface-muted));
    color: rgb(var(--color-text-muted));
    font-size: 11px;
    font-weight: 500;
  }

  .team-compensation__period-status {
    padding: 2px 10px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
  }

  .team-compensation__period-status--open {
    border-color: rgb(var(--color-success) / 0.25);
    background: rgb(var(--color-success) / 0.05);
    color: rgb(var(--color-success));
  }

  .team-compensation__period-status--closed {
    background: rgb(var(--color-surface-muted));
    color: rgb(var(--color-text-muted));
  }

  .team-compensation__period-status--locked {
    border-color: rgb(var(--color-warning) / 0.3);
    background: rgb(var(--color-warning) / 0.06);
    color: rgb(var(--color-warning));
  }

  .team-compensation__summary-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
  }

  .team-compensation__summary-card {
    display: flex;
    min-width: 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
    padding: 16px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 12px;
    background: rgb(var(--color-surface));
  }

  .team-compensation__card-topline {
    display: flex;
    width: 100%;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }

  .team-compensation__card-label {
    margin: 0;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .team-compensation__summary-card > strong {
    margin-top: 4px;
    font-size: 22px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .team-compensation__summary-card > span {
    color: rgb(var(--color-text-muted));
    font-size: 11px;
  }

  .team-compensation__exception-count,
  .team-compensation__partial {
    display: inline-flex;
    margin-top: 4px;
    padding: 2px 9px;
    border: 1px solid rgb(var(--color-warning) / 0.3);
    border-radius: 999px;
    background: rgb(var(--color-warning) / 0.07);
    color: rgb(var(--color-warning)) !important;
    font-size: 11px;
    font-weight: 600;
  }

  .team-compensation__all-covered {
    color: rgb(var(--color-success)) !important;
    font-weight: 600;
  }

  .team-compensation__draw-link {
    margin-top: 3px;
  }

  .team-compensation__team-section {
    overflow: hidden;
    border: 1px solid rgb(var(--color-border));
    border-radius: 12px;
    background: rgb(var(--color-surface));
  }

  .team-compensation__team-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid rgb(var(--color-border));
  }

  .team-compensation__team-toolbar h2 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
  }

  .team-compensation__controls,
  .team-compensation__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
  }

  .team-compensation__filter {
    padding: 4px 10px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 999px;
    background: rgb(var(--color-surface));
    color: rgb(var(--color-text-muted));
    font-size: 11px;
    cursor: pointer;
  }

  .team-compensation__filter[data-state="selected"] {
    border-color: rgb(var(--color-primary) / 0.4);
    background: rgb(var(--color-selected-bg));
    color: rgb(var(--color-primary));
    font-weight: 600;
  }

  .team-compensation__sort {
    display: flex;
    align-items: center;
    gap: 6px;
    color: rgb(var(--color-text-muted));
    font-size: 11px;
  }

  .team-compensation__sort select {
    min-height: 30px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 8px;
    background: rgb(var(--color-surface));
    color: rgb(var(--color-text));
    font: inherit;
  }

  .team-compensation__hierarchy-badge {
    display: flex;
    align-items: center;
  }

  .team-compensation__hierarchy-label {
    padding: 3px 10px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 999px;
    background: rgb(var(--color-surface-muted));
    color: rgb(var(--color-text-muted));
    font-size: 11px;
    font-weight: 500;
  }

  /* Flat table styles (unchanged from S802) */
  .team-compensation__table {
    width: 100%;
    min-width: 1000px;
    border-collapse: collapse;
    font-size: 12px;
  }

  .team-compensation__table th,
  .team-compensation__table td {
    padding: 10px 12px;
    border-bottom: 1px solid rgb(var(--color-border));
    text-align: left;
    vertical-align: middle;
  }

  .team-compensation__table th {
    background: rgb(var(--color-surface-muted));
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .team-compensation__table tbody tr:last-child td {
    border-bottom: 0;
  }

  /* Hierarchy tree styles (S803) */
  .team-compensation__hierarchy-scroll {
    padding: 8px 0;
  }

  .team-compensation__hierarchy-header {
    display: grid;
    grid-template-columns: 1fr 120px 100px 140px 100px;
    gap: 8px;
    padding: 6px 16px 6px 40px;
    border-bottom: 1px solid rgb(var(--color-border));
    background: rgb(var(--color-surface-muted));
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  .team-compensation__hierarchy-row-wrap {
    padding: 2px 8px;
  }

  .team-compensation__hierarchy-row {
    display: flex;
    align-items: stretch;
    border: 1px solid rgb(var(--color-border));
    border-radius: 8px;
    background: rgb(var(--color-surface));
    transition: background 120ms, border-color 120ms;
  }

  .team-compensation__hierarchy-row--self {
    border-color: rgb(var(--color-primary) / 0.35);
    background: rgb(var(--color-primary) / 0.04);
  }

  .team-compensation__hierarchy-row--highlighted {
    background: rgb(var(--color-primary) / 0.1);
    outline: 2px solid rgb(var(--color-primary) / 0.4);
    outline-offset: -1px;
  }

  .team-compensation__hierarchy-row--exception {
    border-color: rgb(var(--color-warning) / 0.3);
  }

  .team-compensation__hierarchy-chevron {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    flex-shrink: 0;
    background: none;
    border: none;
    border-right: 1px solid rgb(var(--color-border));
    cursor: pointer;
    color: rgb(var(--color-text-muted));
    padding: 0;
  }

  .team-compensation__hierarchy-chevron:hover {
    color: rgb(var(--color-text));
    background: rgb(var(--color-surface-muted));
  }

  .team-compensation__hierarchy-chevron-spacer {
    width: 28px;
    flex-shrink: 0;
  }

  .team-compensation__chevron-icon {
    display: inline-block;
    font-size: 16px;
    line-height: 1;
    transition: transform 150ms ease;
  }

  .team-compensation__hierarchy-row-btn {
    display: grid;
    grid-template-columns: 1fr 120px 100px 140px 100px;
    gap: 8px;
    flex: 1;
    align-items: center;
    padding: 8px 12px;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    font-size: 12px;
    color: rgb(var(--color-text));
    min-width: 0;
  }

  .team-compensation__hierarchy-row-btn:hover {
    background: rgb(var(--color-surface-muted) / 0.6);
  }

  .team-compensation__hierarchy-col--name {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex-wrap: wrap;
  }

  .team-compensation__hierarchy-col--quota,
  .team-compensation__hierarchy-col--attain,
  .team-compensation__hierarchy-col--payout,
  .team-compensation__hierarchy-col--coverage {
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .team-compensation__hierarchy-name {
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .team-compensation__hierarchy-name--self {
    font-weight: 700;
    color: rgb(var(--color-primary));
  }

  .team-compensation__self-badge {
    display: inline-flex;
    padding: 1px 7px;
    border-radius: 999px;
    background: rgb(var(--color-primary) / 0.12);
    color: rgb(var(--color-primary));
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.03em;
    white-space: nowrap;
    flex-shrink: 0;
  }

  /* calcModeLabel pills (S803 — ported from ManagerShell) */
  .tc-pill {
    display: inline-flex;
    padding: 1px 7px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 600;
    flex-shrink: 0;
    white-space: nowrap;
  }

  .tc-pill--rollup {
    background: rgb(var(--color-info) / 0.15);
    color: rgb(var(--color-info));
  }

  .tc-pill--hybrid {
    background: rgb(var(--color-primary) / 0.15);
    color: rgb(var(--color-primary));
  }

  .tc-pill--ic {
    background: rgb(var(--color-surface-muted));
    color: rgb(var(--color-text-muted));
  }

  .tc-pill--exempt {
    background: rgb(var(--color-warning) / 0.15);
    color: rgb(var(--color-warning));
  }

  /* Pagination sentinel and loading */
  .team-compensation__pagination-sentinel {
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .team-compensation__loading-more {
    color: rgb(var(--color-text-muted));
    font-size: 12px;
  }

  .team-compensation__hierarchy-end {
    padding: 12px 16px;
    text-align: center;
    color: rgb(var(--color-text-muted));
    font-size: 11px;
    border-top: 1px solid rgb(var(--color-border));
  }

  /* Totals footer — tfoot row, aligned with table columns */
  .team-compensation__totals-row td {
    padding: 10px 12px;
    border-top: 2px solid rgb(var(--color-border));
    background: rgb(var(--color-surface-muted));
    font-size: 12px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .team-compensation__totals-label-cell {
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .team-compensation__totals-value-cell {
    text-align: left;
    color: rgb(var(--color-text));
  }

  .team-compensation__cell-meta,
  .team-compensation__cell-muted {
    display: block;
    margin-top: 2px;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
  }

  .team-compensation__payout-cell {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
  }

  .team-compensation__coverage-state,
  .team-compensation__amendment-state {
    display: inline-flex;
    padding: 2px 9px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
  }

  .team-compensation__coverage-state--covered {
    border-color: rgb(var(--color-success) / 0.25);
    background: rgb(var(--color-success) / 0.05);
    color: rgb(var(--color-success));
  }

  .team-compensation__coverage-state--exception,
  .team-compensation__coverage-state--unassigned {
    border-color: rgb(var(--color-warning) / 0.3);
    background: rgb(var(--color-warning) / 0.06);
    color: rgb(var(--color-warning));
  }

  .team-compensation__amendment-state {
    border-color: rgb(var(--color-warning) / 0.3);
    background: rgb(var(--color-warning) / 0.06);
    color: rgb(var(--color-warning));
  }

  .team-compensation button:focus-visible,
  .team-compensation select:focus-visible,
  .team-compensation__dialog:focus-visible {
    outline: 2px solid rgb(var(--color-focus-ring));
    outline-offset: 2px;
  }

  .team-compensation__dialog-backdrop {
    position: fixed;
    z-index: 1000;
    inset: 0;
    display: flex;
    justify-content: flex-end;
    background: rgb(0 0 0 / 0.35);
  }

  .team-compensation__dialog {
    width: min(620px, 100%);
    height: 100%;
    overflow-y: auto;
    border-left: 1px solid rgb(var(--color-border));
    background: rgb(var(--color-bg));
    box-shadow: -8px 0 30px rgb(0 0 0 / 0.14);
  }

  .team-compensation__dialog-header {
    position: sticky;
    z-index: 2;
    top: 0;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid rgb(var(--color-border));
    background: rgb(var(--color-surface));
  }

  .team-compensation__dialog-header h2 {
    margin: 0;
    font-size: 14px;
  }

  .team-compensation__dialog-header p {
    margin: 3px 0 0;
    color: rgb(var(--color-text-muted));
    font-size: 11px;
  }

  .team-compensation__dialog-body {
    padding: 16px;
  }

  .team-compensation__error {
    padding: 16px 12px;
    border: 1px solid rgb(var(--color-danger) / 0.2);
    border-radius: 8px;
    background: rgb(var(--color-danger) / 0.05);
  }

  .team-compensation__error h2 {
    margin: 0 0 5px;
    font-size: 13px;
  }

  .team-compensation__error p {
    margin: 0 0 12px;
    color: rgb(var(--color-text-muted));
    font-size: 12px;
  }

  .team-compensation__empty {
    padding: 24px 16px;
    border: 1px dashed rgb(var(--color-border));
    border-radius: 8px;
    background: rgb(var(--color-surface-muted));
    color: rgb(var(--color-text-muted));
    font-size: 12px;
    text-align: center;
  }

  .team-compensation__card-skeleton {
    height: 135px;
    border-radius: 12px;
  }

  .team-compensation__table-skeleton {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }

  .team-compensation__table-head-skeleton {
    height: 36px;
    border-radius: 8px;
  }

  .team-compensation__row-skeleton {
    height: 52px;
    border-radius: 8px;
  }

  @media (max-width: 1080px) {
    .team-compensation__summary-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 620px) {
    .team-compensation__header {
      flex-direction: column;
    }
    .team-compensation__header-signals {
      justify-content: flex-start;
    }
    .team-compensation__summary-grid {
      grid-template-columns: 1fr;
    }
    .team-compensation__team-toolbar,
    .team-compensation__controls {
      align-items: flex-start;
      flex-direction: column;
    }
    .team-compensation__hierarchy-header,
    .team-compensation__hierarchy-row-btn {
      grid-template-columns: 1fr 100px 100px;
    }
    .team-compensation__hierarchy-col--quota,
    .team-compensation__hierarchy-col--coverage {
      display: none;
    }
  }
`;

export default TeamCompensation;
